import { Package, Users, TrendingUp, DollarSign, LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PlanStats } from '../types';
import { formatCurrency, formatNumber } from './StatusBadge';

interface StatItem {
    label: string;
    value: string;
    icon: LucideIcon;
    color: 'blue' | 'emerald' | 'purple' | 'orange';
}

interface StatsCardsProps {
    stats: PlanStats;
}

const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
        blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
        emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
        purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
        orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
    };
    return colors[color] || colors.blue;
};

export const StatsCards = ({ stats }: StatsCardsProps) => {
    const { t } = useTranslation();

    const statsArray: StatItem[] = [
        { label: t('plan.stats.totalPlans'), value: formatNumber(stats.total_plans), icon: Package, color: 'blue' },
        { label: t('plan.stats.activePlans'), value: formatNumber(stats.active_plans), icon: TrendingUp, color: 'emerald' },
        { label: t('plan.stats.totalInvestment'), value: formatCurrency(stats.total_investment), icon: DollarSign, color: 'purple' },
        { label: t('plan.stats.totalInvestors'), value: formatNumber(stats.total_investors), icon: Users, color: 'orange' },
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {statsArray.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-md border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{stat.label}</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stat.value}</p>
                        </div>
                        <div className={`p-3 rounded-md ${getColorClasses(stat.color)}`}>
                            <stat.icon className="h-6 w-6" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};
